// Angular
import { Component, computed, DestroyRef, inject, OnInit, output, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { NgClass } from '@angular/common';
import {
  FormGroup,
  FormControl,
  Validators,
  AsyncValidatorFn,
  AbstractControl,
  ValidationErrors,
  FormArray,
  ReactiveFormsModule,
} from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

// Angular Material
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatTooltipModule } from '@angular/material/tooltip';

// Third Party
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { merge, Subject, forkJoin, Observable, of, delay, map, switchMap } from 'rxjs';
import awsRegions from 'aws-regions';

// Services
import { CustomerService } from '@services/customer.service';
import { UserManagementService } from '@services/user-management.service';

// Models
import {
  AwsRegionInfo,
  Customer,
  CustomerStatus,
  CustomerValidatorProperty,
  INTEGRATIONS_LIST,
  OrgUsersPasswordDeleteDTO,
  RestrictedCustomerProperty,
  UniqueValidator,
} from './../defs';
import { FleetManager } from '@components/account-manager';
import { Stack } from '@app/defs/stack';

// Constants
import { Action, Application, Confirmation, AccessRight, Active } from '@constants/enum';
import { APPLICATIONS_LIST_NO_VI, DEBOUNCE_TIME_SEARCH_FIELDS } from '@constants/common';

// Pipes
import { AllRightsPipe } from '@utils/all-rights.pipe';
import { domainsValidatorFactory, domainValidatorFactory } from '@utils/validators';

// Dialogs
import { CountdownDialogComponent, SuccessDialogComponent } from '@components/dialogs';
import { PasswordDeleteDialogComponent } from '../password-delete-dialog/password-delete-dialog.component';

// Components
import { LoadingSpinnerComponent, FormErrorComponent } from '@components/common-components';

@Component({
  selector: 'app-customer-form',
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatCardModule,
    MatSidenavModule,
    MatTooltipModule,
    RouterLink,
    NgxMatSelectSearchModule,
    NgClass,
    AllRightsPipe,
    LoadingSpinnerComponent,
    FormErrorComponent,
  ],
  templateUrl: './customer-form.component.html',
  styleUrl: './customer-form.component.scss',
})
export class CustomerFormComponent implements OnInit {
  private customerService = inject(CustomerService);
  private userManagementService = inject(UserManagementService);
  private matDialog = inject(MatDialog);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);

  readonly ENABLED_BILLABLE: string = 'Billable';
  readonly MAX_DOMAIN_NAME_LENGTH = 25;

  readonly accessRight = AccessRight;

  readonly pageType = signal<string>(null);

  readonly customer = signal<Customer>(null);
  readonly stacks = signal<Stack[]>([]);
  readonly timeZones = signal<string[]>([]);
  readonly defaultTimeZones = signal<string[]>([]);
  readonly awsRegionsList = signal<AwsRegionInfo[]>([]);
  readonly customerStatusOptions = signal<CustomerStatus[]>([]);

  readonly applicationOptions = APPLICATIONS_LIST_NO_VI;
  readonly selectedApplicationOptions = signal<string[]>([]);
  readonly selectedIntegrationOptions = signal<string[]>([]);

  readonly integrationOptions = Object.values(INTEGRATIONS_LIST);

  readonly defaultName = signal('');
  readonly defaultKey = signal('');
  readonly defaultEmail = signal('');

  readonly loading = signal(true);
  readonly formPrepareError = signal(false);

  readonly active = Object.values(Active); // ['Enabled', 'Disabled']

  readonly action = Action;
  readonly restrictedCustomerProperty = RestrictedCustomerProperty;

  readonly focusState = signal<Record<string, boolean>>({});

  customerForm = new FormGroup({
    id: new FormControl(),
    name: new FormControl('', {
      validators: [Validators.required],
      asyncValidators: [this.uniqueValidator(CustomerValidatorProperty.name)],
    }),
    phone: new FormControl(null),
    email: new FormControl('', {
      validators: [Validators.required, Validators.email],
      asyncValidators: [this.uniqueValidator(CustomerValidatorProperty.email)],
    }),
    address: new FormControl(''),
    timezone: new FormControl('', [Validators.required]),
    tenantGroupName: new FormControl(''),
    status: new FormControl(),
    tenantKey: new FormControl('', {
      validators: [Validators.required],
      asyncValidators: [this.uniqueValidator(CustomerValidatorProperty.key)],
    }),
    vehicleIntelligenceUrl: new FormControl('', [Validators.required]),
    vehicleIntelligenceAccess: new FormControl(true),
    assetIntelligenceAccess: new FormControl(false),
    fleetIntelligenceAccess: new FormControl(false),
    safetyIntelligenceAccess: new FormControl(false),
    businessIntelligenceAccess: new FormControl(false),
    integrationCadAccess: new FormControl(false),
    integrationFleetAccess: new FormControl(false),
    appRegionPrefix: new FormControl(''),
    biRegion: new FormControl(''),
    stack: new FormControl(null, [Validators.required]),
    firstName: new FormControl('', [Validators.required]),
    lastName: new FormControl('', [Validators.required]),
    domains: new FormArray<FormControl<string>>(
      [],
      [Validators.required, Validators.minLength(1), domainsValidatorFactory(100)],
    ),
    hasIdentityProvider: new FormControl(false),
  });

  get customerFormDomains(): FormArray<FormControl<string>> {
    return this.customerForm.get('domains') as FormArray<FormControl<string>>;
  }

  timeZoneSearch: FormControl = new FormControl();

  readonly isApplicationsEnabled = signal<boolean>(true);
  readonly isIntegrationsEnabled = signal<boolean>(true);
  readonly isViUrlEnabled = signal<boolean>(true);
  readonly isAppPrefixEnabled = signal<boolean>(true);

  readonly fleetManagers = signal<FleetManager[]>([]);
  readonly customerOwnerNotFound = signal(false);
  readonly noFleetManagersFound = signal(false);

  subjReload = new Subject<void>();

  readonly originalDomains = signal<string[]>([]);

  readonly domainsChanged = computed(() => {
    const currentDomains = this.customerFormDomains.value;
    return JSON.stringify(this.originalDomains()) !== JSON.stringify(currentDomains);
  });

  // For tabs button
  readonly formValid = output<boolean>();
  readonly isCreateForm = output<boolean>();
  readonly customerKeyChange = output<string>();

  ngOnInit() {
    if (this.route.snapshot.params['type'] === Action.edit) {
      this.pageType.set(Action.edit);
      this.getCustomerById(this.route.snapshot.params['id']);
      this.disableFormValues();
    } else {
      this.pageType.set(Action.create);
      this.isCreateForm.emit(true);
      this.addDomain();
      this.prepareForm();
    }

    this.customerForm.statusChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.formValid.emit(this.customerForm.valid);
    });

    this.setupConditionalValidator(
      ['fleetIntelligenceAccess', 'safetyIntelligenceAccess'],
      'appRegionPrefix',
    );
    this.setupConditionalValidator(['businessIntelligenceAccess'], 'biRegion');
  }

  getCustomerById(id: number) {
    this.customerService
      .getTenantById(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data: Customer) => {
          if (data == null) {
            this.formPrepareError.set(true);
            return;
          }
          this.customer.set(data);
          this.patchForm(this.customer());
          // Only emit on first load
          // Needs to be saved before IDP is enabled
        },
        error: () => this.formPrepareError.set(true),
      });
  }

  // Configure the form based on customer details
  patchForm(customer: Customer) {
    if (Array.isArray(customer.domains) && customer.domains.length > 0) {
      customer.domains.forEach((domain) => {
        this.customerFormDomains.push(
          new FormControl<string>(domain, domainValidatorFactory(this.MAX_DOMAIN_NAME_LENGTH)),
        );
      });
    }

    this.customerForm.patchValue({
      ...customer,
    });

    this.defaultName.set(customer.name);
    this.defaultKey.set(customer.tenantKey);
    this.defaultEmail.set(customer.email);

    this.originalDomains.set([...this.customerFormDomains.value]);

    const filterEnabled = (entries: [boolean, string][]) =>
      entries.filter(([enabled]) => enabled).map(([, label]) => label);

    this.selectedApplicationOptions.set(
      filterEnabled([
        [customer.assetIntelligenceAccess, Application.assetIntelligence],
        [customer.fleetIntelligenceAccess, Application.fleetIntelligence],
        [customer.safetyIntelligenceAccess, Application.safetyIntelligence],
        [customer.businessIntelligenceAccess, Application.businessIntelligence],
      ]),
    );

    this.selectedIntegrationOptions.set(
      filterEnabled([
        [customer.integrationCadAccess, INTEGRATIONS_LIST.CAD.label],
        [customer.integrationFleetAccess, INTEGRATIONS_LIST.FLEET.label],
      ]),
    );
    // Emit customer key for IDP page
    this.customerKeyChange.emit(customer.tenantKey);
    this.prepareForm();
  }

  onSubmit() {
    const customer: Customer = this.customerForm.getRawValue();
    if (this.customerForm.valid) {
      this.loading.set(true);

      if (this.pageType() === Action.create) {
        this.customerService
          .createTenant(customer)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.loading.set(false);
              this.router.navigate(['/customers']);
            },
            error: () => {
              this.loading.set(false);
            },
          });
      } else {
        this.customerService
          .updateTenant(customer)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.loading.set(false);
              this.router.navigate(['/customers']);
            },
            error: () => {
              this.loading.set(false);
            },
          });
      }
    } else this.customerForm.markAllAsTouched();
  }

  disableFormValues() {
    this.customerForm.get('tenantKey')?.disable();
    this.customerForm.get('stack')?.disable();
    this.isViUrlEnabled.set(false);
    this.isAppPrefixEnabled.set(false);
    this.customerForm.get('biRegion')?.disable();
    this.customerForm.get('hasIdentityProvider')?.disable();
    this.isApplicationsEnabled.set(false);
    this.isIntegrationsEnabled.set(false);
  }

  uniqueValidator(propertyName: string): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      if (this.customer()) {
        switch (propertyName) {
          case CustomerValidatorProperty.name: {
            if (control.value === this.customer().name) {
              return of(null);
            }
            break;
          }
          case CustomerValidatorProperty.key: {
            if (control.value === this.customer().tenantKey) {
              return of(null);
            }
            break;
          }
          case CustomerValidatorProperty.email: {
            if (control.value === this.customer().email) {
              return of(null);
            }
            break;
          }
          default: {
            console.error('Case not found for unique switch statement');
            return of({ isNotUnique: true });
          }
        }
      }
      return of(control.value).pipe(
        delay(DEBOUNCE_TIME_SEARCH_FIELDS),
        switchMap((value) => {
          if (!value) {
            return of(null);
          }
          switch (propertyName) {
            case CustomerValidatorProperty.name: {
              return this.customerService.checkCustomerName(value).pipe(
                map((data: UniqueValidator) => {
                  const isResultUnique = !(
                    data && data.name.toLowerCase() != this.defaultName().toLowerCase()
                  );
                  return isResultUnique ? null : { isNotUnique: true };
                }),
              );
            }
            case CustomerValidatorProperty.key: {
              return this.customerService.checkTenantKey(value).pipe(
                map((data: UniqueValidator) => {
                  const isResultUnique = !(
                    data && data.name.toLowerCase() != this.defaultName().toLowerCase()
                  );
                  return isResultUnique ? null : { isNotUnique: true };
                }),
              );
            }
            case CustomerValidatorProperty.email: {
              return this.customerService.checkTenantEmail(value).pipe(
                map((data: UniqueValidator) => {
                  const isResultUnique = !(
                    data && data.name.toLowerCase() != this.defaultName().toLowerCase()
                  );
                  return isResultUnique ? null : { isNotUnique: true };
                }),
              );
            }
          }
        }),
      );
    };
  }

  prepareAwsRegions() {
    this.awsRegionsList.set(awsRegions.list());
  }

  // Prepare dropdowns for form
  prepareForm() {
    this.prepareAwsRegions();
    merge(this.subjReload)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap(() => {
          if (this.pageType() === Action.edit) {
            return forkJoin({
              fleetManagers: this.userManagementService.getFleetManagersForCustomer(
                this.customer().tenantKey,
              ),
              stacks: this.customerService.getAllStacks(),
              timezones: this.customerService.getTimeZones(),
              statuses: this.customerService.getCustomerStatusTypes(),
            });
          } else {
            return forkJoin({
              stacks: this.customerService.getAllStacks(),
              timezones: this.customerService.getTimeZones(),
              statuses: this.customerService.getCustomerStatusTypes(),
            });
          }
        }),
      )
      .subscribe({
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        next: (response: any) => {
          const { fleetManagers, stacks, timezones, statuses } = response;

          if (this.pageType() === Action.edit) {
            if (fleetManagers) {
              this.fleetManagers.set(fleetManagers['rows']);
              this.validateCustomerOwner(this.fleetManagers(), this.customer().email);
            } else {
              this.noFleetManagersFound.set(true);
            }
          }
          if (stacks) {
            this.stacks.set(stacks['entity']);
          }
          if (timezones) {
            this.timeZones.set(timezones['entity']);
            this.defaultTimeZones.set(timezones['entity']);
          }
          if (statuses) {
            this.customerStatusOptions.set(statuses['entity']);
            if (this.customerStatusOptions()[1].name !== this.ENABLED_BILLABLE) {
              this.formPrepareError.set(true);
              console.error('Default status value was not found');
            }
            if (this.pageType() === Action.create) {
              this.customerForm.get('status').setValue(1);
            }
          }
          this.loading.set(false);
        },
        error: () => {
          this.loading.set(false);
          this.formPrepareError.set(true);
        },
      });
    this.subjReload.next();
  }

  validateCustomerOwner(fleetManagers: FleetManager[], customerOwner: string) {
    const found = fleetManagers.find((element) => element.email === customerOwner);
    if (!found) {
      this.customerOwnerNotFound.set(true);
      this.customerForm.get('email').setValue(null);
    }
  }

  setupConditionalValidator(toggleControls: string[], targetControl: string): void {
    const toggles = toggleControls
      .map((name) => this.customerForm.get(name))
      .filter((c): c is AbstractControl => c != null);

    merge(...toggles.map((c) => c.valueChanges))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const control = this.customerForm.get(targetControl);
        const anyEnabled = toggles.some((c) => c.value === true);
        if (anyEnabled) {
          control?.setValidators([Validators.required]);
        } else {
          control?.clearValidators();
        }
        control?.updateValueAndValidity();
      });
  }

  getErrorMessage(name: string) {
    switch (name) {
      case 'name': {
        if (
          this.customerForm.controls['name'].hasError('required') &&
          this.customerForm.controls['name'].touched
        ) {
          return 'You must enter a customer name.';
        } else if (this.customerForm.controls['name'].hasError('isNotUnique')) {
          return 'A customer already exists with that name.';
        }
        return 'Error';
      }
      case 'tenantKey': {
        if (
          this.customerForm.controls['tenantKey'].hasError('required') &&
          this.customerForm.controls['tenantKey'].touched
        ) {
          return 'You must enter a customer key.';
        } else if (this.customerForm.controls['tenantKey'].hasError('isNotUnique')) {
          return 'A customer already exists with that key.';
        }
        return 'Error';
      }
      case 'email': {
        if (this.noFleetManagersFound()) {
          return 'No Fleet Managers found.';
        } else if (!this.customerForm.controls['email'].touched && this.customerOwnerNotFound()) {
          return 'Existing Customer Owner is not a Fleet Manager.';
        } else if (
          this.customerForm.controls['email'].hasError('required') &&
          this.customerForm.controls['email'].touched
        ) {
          return 'You must enter a customer email.';
        } else if (
          this.customerForm.controls['email'].hasError('email') &&
          this.customerForm.controls['email'].touched
        ) {
          return 'You must enter a valid email.';
        } else if (this.customerForm.controls['email'].hasError('isNotUnique')) {
          return 'A customer already exists with that email.';
        }
        return 'Error';
      }
      case 'timezone': {
        if (this.customerForm.controls['timezone'].touched) {
          return 'You must choose a timezone.';
        }
        return 'Error';
      }
      case 'vehicleIntelligenceUrl': {
        if (this.customerForm.controls['vehicleIntelligenceUrl'].touched) {
          return 'You must enter a URL.';
        }
        return 'Error';
      }
      case 'stack': {
        if (this.customerForm.controls['stack'].touched) {
          return 'You must choose a stack.';
        }
        return 'Error';
      }
      case 'firstName': {
        if (this.customerForm.controls['firstName'].touched) {
          return 'You must enter a name.';
        }
        return 'Error';
      }
      case 'lastName': {
        if (this.customerForm.controls['lastName'].touched) {
          return 'You must enter a name.';
        }
        return 'Error';
      }
      case 'biRegion': {
        if (this.customerForm.controls['biRegion'].touched) {
          return 'You must select an AWS Region for BI.';
        }
        return 'Error';
      }
      case 'appRegionPrefix': {
        if (this.customerForm.controls['appRegionPrefix'].touched) {
          return 'You must enter a URL prefix for FI.';
        }
        return 'Error';
      }
    }
    return null;
  }

  filterTimeZones(word: string) {
    this.timeZones.set(
      this.defaultTimeZones().filter((element) =>
        element.toLowerCase().includes(word.toLowerCase()),
      ),
    );
  }

  openedChange(opened: boolean) {
    if (!opened) {
      this.timeZones.set(this.defaultTimeZones());
    }
  }

  noSpace(event: KeyboardEvent) {
    if (event.code === 'Space') {
      event.preventDefault();
    }
  }

  selectNewCustomerOwner(fleetManager: FleetManager) {
    this.customerForm.get('firstName').setValue(fleetManager.firstName);
    this.customerForm.get('lastName').setValue(fleetManager.lastName);
  }

  areYouSureKey(type: RestrictedCustomerProperty) {
    if (this.pageType() == Action.edit) {
      const dialogRef = this.matDialog.open(CountdownDialogComponent, {
        data: { type: type },
      });
      dialogRef
        .afterClosed()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((result) => {
          if (result == Confirmation.confirm) {
            switch (type) {
              case RestrictedCustomerProperty.stack: {
                this.customerForm.get('stack')?.enable();
                break;
              }
              case RestrictedCustomerProperty.viUrl: {
                this.isViUrlEnabled.set(true);
                break;
              }
              case RestrictedCustomerProperty.appPrefix: {
                this.isAppPrefixEnabled.set(true);
                break;
              }
              case RestrictedCustomerProperty.biRegion: {
                this.customerForm.get('biRegion')?.enable();
                break;
              }
              case RestrictedCustomerProperty.idp: {
                this.customerForm.get('hasIdentityProvider')?.enable();
                break;
              }
              case RestrictedCustomerProperty.applications: {
                this.isApplicationsEnabled.set(true);
                break;
              }
              case RestrictedCustomerProperty.integrations: {
                this.isIntegrationsEnabled.set(true);
                break;
              }
              default:
                console.error('Type not found');
                break;
            }
          }
        });
    }
  }

  onInputFocus(formValue: string) {
    this.focusState.update((state) => ({ ...state, [formValue]: true }));
  }

  onInputBlur(formValue: string) {
    this.focusState.update((state) => ({ ...state, [formValue]: false }));
  }

  trimFieldAndLowerCase(controlName: string) {
    const control = this.customerForm.get(controlName);
    if (control?.value) {
      control.setValue(control.value.trim().toLowerCase());
    }
  }

  onApplicationOptionSelected(applications: string[]) {
    const accessControls: { [key: string]: string } = {
      assetIntelligenceAccess: Application.assetIntelligence,
      fleetIntelligenceAccess: Application.fleetIntelligence,
      safetyIntelligenceAccess: Application.safetyIntelligence,
      businessIntelligenceAccess: Application.businessIntelligence,
    };

    Object.entries(accessControls).forEach(([controlName, application]) => {
      this.customerForm.get(controlName).setValue(applications.includes(application));
    });
  }

  onIntegrationsOptionSelected(applications: string[]) {
    const accessControls: { [key: string]: string } = {
      integrationCadAccess: INTEGRATIONS_LIST.CAD.label,
      integrationFleetAccess: INTEGRATIONS_LIST.FLEET.label,
    };

    Object.entries(accessControls).forEach(([controlName, integration]) => {
      this.customerForm.get(controlName).setValue(applications.includes(integration));
    });
  }

  compareStacks(s1: Stack | null, s2: Stack | null): boolean {
    return s1 && s2 ? s1.name === s2.name : s1 === s2;
  }

  addDomain() {
    const control = new FormControl<string>(
      '',
      domainValidatorFactory(this.MAX_DOMAIN_NAME_LENGTH),
    );
    this.customerFormDomains.push(control);
  }

  removeDomain(index: number) {
    if (this.customerFormDomains.length > 1) {
      this.customerFormDomains.removeAt(index);
    }
  }

  clearPasswords() {
    const dialogRef = this.matDialog.open(PasswordDeleteDialogComponent);
    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data) => {
        if (!data) return;
        if (!data.includeCrew && !data.includeUsers) return;

        const orgUsersPasswordDeleteDTO: OrgUsersPasswordDeleteDTO = {
          domains: this.customerForm.get('domains').value,
          includeCrew: data.includeCrew,
          includeUsers: data.includeUsers,
        };
        const customerKey = this.customerForm.get('tenantKey').value;
        this.loading.set(true);
        this.userManagementService
          .deletePasswords(customerKey, orgUsersPasswordDeleteDTO)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.loading.set(false);
              this.openSuccessDialog(customerKey);
            },
            error: () => this.loading.set(false),
          });
      });
  }

  openSuccessDialog(customerKey: string) {
    this.matDialog.open(SuccessDialogComponent, {
      data: {
        title: `Passwords Deleted!`,
        message: `You have successfully deleted all the passwords for ${customerKey}.`,
        button: 'Great!',
      },
    });
  }
}
